import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { List } from '../../contexts/ListsContext'
import { resetState } from '../actions'

interface ListsState {
    byId: Record<string, List>
    allIds: string[]
    loading: boolean
    error: string | null
    // A just-created section to scroll into view once it renders (SCD-159). Cleared after scroll.
    scrollToListId: string | null
}

const initialState: ListsState = {
    byId: {},
    allIds: [],
    loading: true,
    error: null,
    scrollToListId: null
}

const listsSlice = createSlice({
    name: 'lists',
    initialState,
    reducers: {
        // Saga triggers
        startListening: (_state, _action: PayloadAction<{ projectId?: string; workspaceId: string }>) => {},
        stopListening: () => {},
        createListSaga: (_state, _action: PayloadAction<{ projectId: string; name: string; workspaceId: string; afterListId?: string }>) => {},
        updateListSaga: (_state, _action: PayloadAction<{ listId: string; data: Partial<Pick<List, 'name' | 'order'>> }>) => {},
        deleteListSaga: (_state, _action: PayloadAction<string>) => {},
        transferListSaga: (_state, _action: PayloadAction<{ listId: string; newProjectId: string }>) => {},

        // State mutations
        setLists: (state, action: PayloadAction<List[]>) => {
            state.byId = {}
            state.allIds = []
            action.payload.forEach(list => {
                state.byId[list.id] = list
                state.allIds.push(list.id)
            })
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        addListLocal: (state, action: PayloadAction<List>) => {
            const list = action.payload
            state.byId[list.id] = list
            if (!state.allIds.includes(list.id)) {
                state.allIds.push(list.id)
            }
        },
        updateListLocal: (state, action: PayloadAction<{ listId: string; data: Partial<List> }>) => {
            const { listId, data } = action.payload
            if (state.byId[listId]) {
                Object.assign(state.byId[listId], data)
            }
        },
        removeListLocal: (state, action: PayloadAction<string>) => {
            delete state.byId[action.payload]
            state.allIds = state.allIds.filter(id => id !== action.payload)
        },
        setScrollToListId: (state, action: PayloadAction<string | null>) => {
            state.scrollToListId = action.payload
        },
        clearLists: (state) => {
            state.byId = {}
            state.allIds = []
            state.loading = true
            state.error = null
            state.scrollToListId = null
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetState, () => initialState)
    }
})

export const {
    startListening,
    stopListening,
    createListSaga,
    updateListSaga,
    deleteListSaga,
    transferListSaga,
    setLists,
    addListLocal,
    updateListLocal,
    removeListLocal,
    setScrollToListId,
    setLoading,
    setError,
    clearLists
} = listsSlice.actions

export default listsSlice.reducer
